/**
 * Build a branded, email-client-safe "Just Listed" email from a real CRM
 * property record (address, photos, features + the listing agent). The output
 * is a full <!doctype> document, so the send engine forwards it untouched, and
 * it seeds the drag-and-drop builder (design_json is cleared) so the agent can
 * keep customising it afterwards.
 *
 * Brand values mirror EmailBuilder.jsx / core.services.email_templates so the
 * result matches every other Collings send.
 */
// Marks the end of the white card, so the Off Market / preferences blocks
// land under the listing and above the footer instead of below the whole
// email (MP, Monday 13056948901). Same anchor the BE template writes -
// see messageBlocks.EXTRAS_ANCHOR.
import { EXTRAS_ANCHOR } from "./messageBlocks";

const BRAND = "#2DC8DB";
const BRAND_DARK = "#25AFC0";
const LOGO_URL = "https://crm.collings.com.au/collingslogo-removebg-preview.png";
const WEBSITE_URL = "https://www.collings.com.au";
const SUPPORT_EMAIL = "info@collings.com.au";
const UNSUB_TOKEN = "%%UNSUBSCRIBE_URL%%";

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** A "3 bed · 2 bath · 1 car" plain-text line (used for the preview snippet). */
function featureLine(p) {
  const bits = [];
  if (p.beds) bits.push(`${p.beds} bed`);
  if (p.baths) bits.push(`${p.baths} bath`);
  if (p.cars) bits.push(`${p.cars} car`);
  if (p.land_size) bits.push(`${Math.round(p.land_size)}m² land`);
  return bits.join(" &nbsp;·&nbsp; ");
}

/** The feature row for the property card, with bed / shower / car icons.
 * Uses emoji (not SVG) because Gmail/Outlook strip inline SVG but render emoji
 * consistently. Zero counts are dropped. */
function featureIconsHtml(p) {
  const bits = [];
  if (p.beds) bits.push(`\u{1F6CF}\u{FE0F}&nbsp;${p.beds}`); // bed
  if (p.baths) bits.push(`\u{1F6BF}&nbsp;${p.baths}`); // shower
  if (p.cars) bits.push(`\u{1F697}&nbsp;${p.cars}`); // car
  if (p.land_size) bits.push(`\u{1F4D0}&nbsp;${Math.round(p.land_size)}m²`); // land
  if (!bits.length) return "";
  return bits
    .map(
      (b) =>
        `<span style="display:inline-block;margin-right:18px;font-size:15px;color:#1f2933;white-space:nowrap;">${b}</span>`
    )
    .join("");
}

/** Whether a listing is sold/leased (closed), so copy reads "Just Sold". */
function isClosed(p) {
  const s = (p.status || "").toLowerCase();
  return s.includes("sold") || s.includes("leased");
}
function isLeased(p) {
  return (p.status || "").toLowerCase().includes("leased");
}

/** A default subject/preview for a property send (agent can edit them). */
export function propertyEmailMeta(p) {
  const suburb = p.suburb || p.state || "your area";
  const verb = isClosed(p) ? (isLeased(p) ? "Just leased" : "Just sold") : "Just listed";
  return {
    subject: p.address ? `${verb}: ${p.address}` : `${verb} in ${suburb}`,
    preview_text: `${p.price_label || ""} · ${featureLine(p).replace(/&nbsp;·&nbsp;/g, "·").replace(/&nbsp;/g, " ")}`.trim(),
  };
}

/** A default lead-in line matching the listing's status. */
export function propertyEmailIntro(p) {
  if (isClosed(p)) {
    const word = isLeased(p) ? "leased" : "sold";
    return `Hi {{FirstName}}, we've just ${word} another property and wanted to share the result with you.`;
  }
  return "Hi {{FirstName}}, a new property has just come to market and I thought of you straight away.";
}

/** Default subject/preview for a multi-listing send (agent can edit them). */
export function propertyListEmailMeta(list = []) {
  const n = list.length;
  return {
    subject: "New listings from Collings",
    preview_text: n ? `A selection of ${n} ${n === 1 ? "property" : "properties"} for you` : "",
  };
}

/** Default lead-in for a multi-listing send. */
export function propertyListEmailIntro() {
  return "Hi {{FirstName}}, here are a few listings I thought you'd want to see.";
}

/**
 * The same Just Listed message as a text, built from the listing record.
 *
 * Christian Gravias, 28 Aug 2026: "If you click Email or SMS button please
 * create an auto template of this property being just listed."
 *
 * Kept to two credits, which is the floor for any text carrying a link: the
 * backend adds ~52 characters of UTM tagging to a collings.com.au link and
 * ~55 of opt-out clause (see smsCredits.asSent), and that is most of a
 * 160-character part before a word of the message. So this carries the address,
 * the features, the price and the link, and nothing else. Plain ASCII on
 * purpose - a single emoji or curly quote turns the whole message Unicode and
 * drops the part size from 160 characters to 70, doubling the bill.
 *
 * Deliberately UNSIGNED. The composer appends the chosen agent's own contact
 * block to every SMS (applySenderAgent -> buildSmsAgentBlock), so a sign-off
 * here printed the agent's name twice and cost about 20 billed characters for
 * the privilege.
 *
 * @param p the email-data payload for the listing
 */
export function buildPropertyJustListedSms(p) {
  const verb = isClosed(p) ? (isLeased(p) ? "Just leased" : "Just sold") : "Just listed";
  const where = p.address || p.suburb || "a new listing";
  // featureLine() is built for HTML, so it separates with non-breaking spaces.
  // Swap the whole separator, spaces included, or the text reads "3 bed ,  2
  // bath" with the gaps left behind.
  const features = featureLine(p)
    .replace(/\s*&nbsp;·&nbsp;\s*/g, ", ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const bits = [`Hi {{FirstName}}, ${verb.toLowerCase()}: ${where}.`];
  if (features) bits.push(`${features}.`);
  if (!isClosed(p) && p.price_label) bits.push(`${p.price_label}.`);
  if (p.listing_url) bits.push(p.listing_url);

  return bits.join(" ");
}

/**
 * Removing the picked property has to take its content back out of the email -
 * leaving it there meant the preview still showed the old listing until the
 * whole page was refreshed (Joshua Diokno, 2026-08-05).
 *
 * @param campaign the live campaign (html_body / design_json / preview_text)
 * @param applied   what the picker wrote, plus what was there before it:
 *                  { html, preview_text, prev_html, prev_design_json, prev_preview_text }
 * @returns { untouched, patch } - `untouched` is false once the agent has edited
 *          the generated body, so the caller can confirm before binning it.
 *          `patch` restores whatever the body was before the property was picked
 *          (usually empty), and only hands back the preview snippet WE filled in.
 */
export function removePropertyPatch(campaign = {}, applied = {}) {
  const ourPreview = campaign.preview_text === applied.preview_text;
  return {
    untouched: (campaign.html_body || "") === (applied.html || ""),
    patch: {
      html_body: applied.prev_html || "",
      design_json: applied.prev_design_json || {},
      preview_text: ourPreview ? applied.prev_preview_text || "" : campaign.preview_text || "",
    },
  };
}

function agentSignatureHtml(agent) {
  if (!agent) return "";
  const rows = [];
  if (agent.phone) {
    rows.push(
      `<div style="font-size:13px;color:#556;margin-top:2px;">${esc(agent.phone)}</div>`
    );
  }
  if (agent.email) {
    rows.push(
      `<div style="font-size:13px;margin-top:2px;"><a href="mailto:${esc(agent.email)}" style="color:${BRAND_DARK};text-decoration:none;">${esc(agent.email)}</a></div>`
    );
  }
  const photoCell = agent.photo
    ? `<td width="64" style="padding-right:14px;vertical-align:top;">
         <img src="${esc(agent.photo)}" alt="${esc(agent.name)}" width="60" height="60" style="width:60px;height:60px;border-radius:50%;object-fit:cover;display:block;" />
       </td>`
    : "";
  return `<table style="width:100%;max-width:560px;margin:18px auto 0;border-collapse:collapse;border-top:1px solid #e5e9ec;">
      <tr><td style="padding:16px 4px 4px;font-family:Arial,sans-serif;">
        <table style="border-collapse:collapse;"><tr>
          ${photoCell}
          <td style="vertical-align:top;">
            <div style="font-size:15px;font-weight:bold;color:#1f2933;">${esc(agent.name)}</div>
            ${agent.title ? `<div style="font-size:13px;color:#556;">${esc(agent.title)}</div>` : ""}
            <div style="font-size:13px;color:#556;">Collings Real Estate</div>
            ${rows.join("")}
          </td>
        </tr></table>
      </td></tr>
    </table>`;
}

/** Format the listing description for email.
 *
 * CRM descriptions (especially commercial) are often a run of "* point * point"
 * bullets on one line. Dumped as a single paragraph that reads as a wall of
 * asterisks, so we detect that shape and render a clean bullet list. Plain
 * prose is split into paragraphs on blank lines instead. Any dangling truncated
 * fragment (e.g. a trailing "..." left by a server-side cut) is dropped. */
function descriptionHtml(desc) {
  const text = (desc || "").replace(/\r\n?/g, "\n").trim();
  if (!text) return "";
  const pStyle =
    "font-size:15px;line-height:1.6;color:#1f2933;max-width:560px;margin:18px auto 0;";

  // Render prose as one <p> per blank-line-separated block (single newlines
  // inside a block collapse to spaces).
  const proseHtml = (block) =>
    block
      .split(/\n{2,}/)
      .map((s) => s.replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .map((para) => `<p style="${pStyle}">${esc(para)}</p>`)
      .join("");

  const firstStar = text.indexOf("*");
  const starCount = (text.match(/\*/g) || []).length;

  // Mixed prose + "* " bullets: keep any lead-in prose as paragraphs, then list
  // the bullets. Pure-prose descriptions just fall through to proseHtml.
  if (starCount >= 2 && firstStar !== -1) {
    const lead = text.slice(0, firstStar).trim();
    const items = text
      .slice(firstStar)
      .split("*")
      .map((s) => s.replace(/\s+/g, " ").trim().replace(/\s*\.\.\.$/, "").trim())
      .filter((s) => s.length > 1);
    if (items.length) {
      const lis = items
        .map((s) => `<li style="margin:0 0 6px;">${esc(s)}</li>`)
        .join("");
      return (
        (lead ? proseHtml(lead) : "") +
        `<ul style="${pStyle}padding-left:20px;">${lis}</ul>`
      );
    }
  }

  return proseHtml(text);
}

/** Additional photos as a 2-up grid (skips the hero, already shown up top). */
function galleryHtml(images) {
  const extra = (images || []).slice(1, 5);
  if (!extra.length) return "";
  const tds = extra.map(
    (src) =>
      `<td width="50%" valign="top" style="padding:4px;">
         <img src="${esc(src)}" alt="Property photo" style="width:100%;display:block;border-radius:8px;" />
       </td>`
  );
  const rows = [];
  for (let i = 0; i < tds.length; i += 2) {
    rows.push(`<tr>${tds[i]}${tds[i + 1] || '<td width="50%"></td>'}</tr>`);
  }
  return `<table style="width:100%;max-width:560px;margin:6px auto 0;border-collapse:collapse;">${rows.join("")}</table>`;
}

/** Build the inner content (property card) for one listing.
 *
 * `includeSignature` is false when several cards share one signature at the end
 * of the email (a multi-listing send), so the agent block is not repeated under
 * every card. */
function propertyInnerHtml(p, { intro, includeSignature = true } = {}) {
  const hero = (p.images && p.images[0]) || null;
  const icons = featureIconsHtml(p);
  const href = p.listing_url || WEBSITE_URL;
  return `
    ${intro ? `<p style="font-size:15px;line-height:1.6;color:#1f2933;margin:0 0 16px;">${esc(intro)}</p>` : ""}
    <table style="width:100%;max-width:560px;margin:0 auto;border-collapse:collapse;border:1px solid #e5e9ec;border-radius:10px;overflow:hidden;">
      ${hero ? `<tr><td><a href="${esc(href)}" style="text-decoration:none;"><img src="${esc(hero)}" alt="${esc(p.address)}" style="width:100%;display:block;" /></a></td></tr>` : ""}
      <tr><td style="padding:18px;font-family:Arial,sans-serif;">
        <div style="font-size:19px;font-weight:bold;color:#1f2933;">${esc(p.address)}</div>
        ${p.suburb ? `<div style="font-size:14px;color:#556;margin-top:2px;">${esc([p.suburb, p.state].filter(Boolean).join(", "))}</div>` : ""}
        ${icons ? `<div style="margin-top:10px;">${icons}</div>` : ""}
        <div style="font-size:17px;font-weight:bold;color:${BRAND_DARK};margin-top:10px;">${esc(p.price_label || "")}</div>
      </td></tr>
    </table>
    ${galleryHtml(p.images)}
    ${descriptionHtml(p.description)}
    <table style="border-collapse:collapse;margin:22px auto 4px;"><tr>
      <td style="border-radius:8px;background:${BRAND};padding:12px 30px;text-align:center;">
        <a href="${esc(href)}" style="color:#ffffff;font-family:Arial,sans-serif;font-size:15px;font-weight:bold;text-decoration:none;">View the listing</a>
      </td>
    </tr></table>
    ${includeSignature ? agentSignatureHtml(p.agent) : ""}
  `;
}

/** A subtle divider between stacked property cards in a multi-listing email. */
const CARD_SEPARATOR =
  '<div style="height:1px;background:#e5e9ec;max-width:560px;margin:32px auto;"></div>';

/** Full email document for one property OR a list of properties.
 *
 * A single property renders exactly as before (its own card + signature). Two or
 * more render as stacked cards - each with its own photos, details and "View the
 * listing" button - followed by a SINGLE agent signature at the end, taken from
 * the first listing's agent (Michael Piccoli, Web Dev Team, 2026-09-23). `intro`
 * (optional) is a lead-in line shown once above the first card. */
export function buildPropertyEmailHtml(pOrList, opts = {}) {
  const list = Array.isArray(pOrList) ? pOrList.filter(Boolean) : [pOrList].filter(Boolean);
  let inner;
  if (list.length <= 1) {
    inner = propertyInnerHtml(list[0] || {}, opts);
  } else {
    const cards = list.map((p, i) =>
      propertyInnerHtml(p, {
        intro: i === 0 ? opts.intro : undefined,
        includeSignature: false,
      })
    );
    inner = cards.join(CARD_SEPARATOR) + agentSignatureHtml((list[0] || {}).agent);
  }
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
</head>
<body style="margin:0;padding:0;background:#f4f6f8;font-family:Arial,sans-serif;">
  <!-- Single centered 600px column so the logo, body and footer all line up -->
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:#f4f6f8;">
    <tr><td align="center" style="padding:24px 12px;">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;border-collapse:collapse;background:#ffffff;border-radius:12px;overflow:hidden;">
        <tr><td style="padding:24px;text-align:center;border-bottom:1px solid #eef1f3;">
          <img src="${LOGO_URL}" alt="Collings Real Estate" style="height:40px;max-width:220px;" />
        </td></tr>
        <tr><td style="padding:24px;">
          ${inner}
        </td></tr>
        ${EXTRAS_ANCHOR}
      </table>
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;border-collapse:collapse;">
        <tr><td style="padding:22px 16px;text-align:center;font-family:Arial,sans-serif;font-size:12px;color:#8a97a3;">
          <div>Collings Real Estate</div>
          <div style="margin-top:6px;">
            <a href="mailto:${SUPPORT_EMAIL}" style="color:${BRAND_DARK};text-decoration:none;">${SUPPORT_EMAIL}</a>
            &nbsp;·&nbsp;
            <a href="${WEBSITE_URL}" style="color:${BRAND_DARK};text-decoration:none;">collings.com.au</a>
          </div>
          <div style="margin-top:10px;">
            You're receiving this because you're a Collings contact.
            <a href="${UNSUB_TOKEN}" style="color:${BRAND_DARK};text-decoration:underline;">Unsubscribe</a>
          </div>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}
